import { c as _c } from "react/compiler-runtime";
// Centralised route table. Pages live under src/pages/<name>.tsx; route
// definitions stay here so the structure of the app is one-glance
// readable. Everything except /login sits behind <RequireAuth>, which gates
// on the auth store and redirects unauthenticated visitors to the login page.
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router";
import { firstAllowedPath, hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";
import { ApplicationsPage } from "./pages/applications";
import { ApplicationCardPage } from "./pages/application-card";
import { CatalogImportJobPage } from "./pages/catalog-import-job";
import { CatalogImportPage } from "./pages/catalog-import";
import { CartsPage } from "./pages/carts";
import { CategoriesPage } from "./pages/categories";
import { CommissionsPage } from "./pages/commissions";
import { DashboardPage } from "./pages/dashboard";
import { FinancesPage } from "./pages/finances";
import { DeliverySettingsPage } from "./pages/delivery-settings";
import { FeedPage } from "./pages/feed";
import { GeoSettingsPage } from "./pages/geo-settings";
import { IdeaEditorPage } from "./pages/idea-editor";
import { IdeasPage } from "./pages/ideas";
import { InspirationFeedPage } from "./pages/inspiration-feed";
import { IntegrationsPage } from "./pages/integrations";
import { AdminLayout } from "./pages/layout";
import { LoginPage } from "./pages/login";
import { AccessDeniedPage, NotFoundPage } from "./pages/not-found";
import { OrdersPage } from "./pages/orders";
import { ProductDetailPage } from "./pages/product-detail";
import { PlatformRolesPage } from "./pages/platform-roles";
import { NotificationsPage } from "./pages/notifications";
import { EmailPage } from "./pages/email";
import { NotificationCampaignComposePage, NotificationCampaignDetailPage, NotificationCampaignsPage } from "./pages/notification-campaigns";
import { ProductsPage } from "./pages/products";
import { PromosAccess, PromosPage } from "./pages/promos";
import { PromoDetailPage } from "./pages/promo-detail";
import { PlatformPromoCampaignsPage } from "./pages/platform-promo-campaigns";
import { PromoApplicationsPage } from "./pages/promo-applications";
import { ReviewComplaintsPage } from "./pages/review-complaints";
import { PartnerDetailPage } from "./pages/partner-detail";
import { UgcArticleDetailPage } from "./pages/ugc-article-detail";
import { SellerClientsPage } from "./pages/seller-clients";
import { AdCampaignsPage } from "./pages/ad-campaigns";
import { AdCampaignDetailPage } from "./pages/ad-campaign-detail";
import { ReportsPage } from "./pages/reports";
import { CompliancePage } from "./pages/compliance";
import { ReturnsPage } from "./pages/returns";
import { SellerDetailPage } from "./pages/seller-detail";
import { ShopDetailPage } from "./pages/shop-detail";
import { ShopsPage } from "./pages/shops";
import { AuditPage } from "./pages/audit";
import { SupportPage } from "./pages/support";
import { TopBuyersPage } from "./pages/top-buyers";
import { UserDetailPage } from "./pages/user-detail";
import { UsersPage } from "./pages/users";
function RequireAuth(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  const status = useAuth(_temp);
  const location = useLocation();
  if (status === "loading") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (status === "anonymous") {
    let t1;
    if ($[1] !== location) {
      t1 = <Navigate to="/login" replace={true} state={{
        from: location
      }} />;
      $[1] = location;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  return children;
}
function _temp(s) {
  return s.status;
}
function RequirePermission(t0) {
  const $ = _c(1);
  const {
    permission,
    children
  } = t0;
  const user = useAuth(_temp2);
  if (!hasAdminPermission(user, permission)) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <AccessDeniedPage />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  return children;
}
function _temp2(s) {
  return s.user;
}
const page = (permission: string, element: ReactNode) => <RequirePermission permission={permission}>{element}</RequirePermission>;
function HomeRedirect() {
  const $ = _c(4);
  const user = useAuth(_temp3);
  let t0;
  if ($[0] !== user) {
    t0 = firstAllowedPath(user);
    $[0] = user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <Navigate to={t0} replace={true} />;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function _temp3(s) {
  return s.user;
}
export function AppRoutes() {
  const $ = _c(52);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Route path="/login" element={<LoginPage />} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <RequireAuth><AdminLayout><Outlet /></AdminLayout></RequireAuth>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Route path="/" element={<HomeRedirect />} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Route path="/dashboard" element={page("page.dashboard", <DashboardPage />)} />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Route path="/finances" element={page("page.finances", <FinancesPage />)} />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Route path="/finances/commissions" element={<Navigate to="/commissions" replace={true} />} />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Route path="/commissions" element={page("page.finances", <CommissionsPage />)} />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Route path="/reports" element={page("page.reports", <ReportsPage />)} />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Route path="/applications" element={page("page.applications", <ApplicationsPage />)} />;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Route path="/applications/:id" element={page("page.applications", <ApplicationCardPage />)} />;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Route path="/notifications" element={<NotificationsPage />} />;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Route path="/notification-campaigns" element={page("page.notification_campaigns", <NotificationCampaignsPage />)} />;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Route path="/notification-campaigns/new" element={page("page.notification_campaigns", <NotificationCampaignComposePage />)} />;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Route path="/notification-campaigns/:id" element={page("page.notification_campaigns", <NotificationCampaignDetailPage />)} />;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Route path="/email" element={page("page.integrations", <EmailPage />)} />;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Route path="/products" element={page("page.products", <ProductsPage />)} />;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Route path="/products/:id" element={page("page.products", <ProductDetailPage />)} />;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Route path="/review-complaints" element={page("page.products", <ReviewComplaintsPage />)} />;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  let t18;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Route path="/catalog-import" element={page("page.catalog_import", <CatalogImportPage />)} />;
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  let t19;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Route path="/catalog-import/:id" element={page("page.catalog_import", <CatalogImportJobPage />)} />;
    $[19] = t19;
  } else {
    t19 = $[19];
  }
  let t20;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Route path="/categories" element={page("page.categories", <CategoriesPage />)} />;
    $[20] = t20;
  } else {
    t20 = $[20];
  }
  let t21;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Route path="/compliance" element={page("page.products", <CompliancePage />)} />;
    $[21] = t21;
  } else {
    t21 = $[21];
  }
  let t22;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Route path="/inspiration-feed" element={page("page.inspiration_feed", <InspirationFeedPage />)} />;
    $[22] = t22;
  } else {
    t22 = $[22];
  }
  let t23;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Route path="/feed" element={page("page.inspiration_feed", <FeedPage />)} />;
    $[23] = t23;
  } else {
    t23 = $[23];
  }
  let t24;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Route path="/ideas" element={page("page.ideas", <IdeasPage />)} />;
    $[24] = t24;
  } else {
    t24 = $[24];
  }
  let t25;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Route path="/ideas/new" element={page("action.ideas.manage", <IdeaEditorPage />)} />;
    $[25] = t25;
  } else {
    t25 = $[25];
  }
  let t26;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Route path="/ideas/:id/edit" element={page("action.ideas.manage", <IdeaEditorPage />)} />;
    $[26] = t26;
  } else {
    t26 = $[26];
  }
  let t27;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Route path="/orders" element={page("page.orders", <OrdersPage />)} />;
    $[27] = t27;
  } else {
    t27 = $[27];
  }
  let t28;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Route path="/returns" element={page("page.orders", <ReturnsPage />)} />;
    $[28] = t28;
  } else {
    t28 = $[28];
  }
  let t29;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Route path="/carts" element={page("page.carts", <CartsPage />)} />;
    $[29] = t29;
  } else {
    t29 = $[29];
  }
  let t30;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Route path="/platform-promo-campaigns" element={<PromosAccess><PlatformPromoCampaignsPage /></PromosAccess>} />;
    $[30] = t30;
  } else {
    t30 = $[30];
  }
  let t31;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Route path="/promo-applications" element={<PromosAccess><PromoApplicationsPage /></PromosAccess>} />;
    $[31] = t31;
  } else {
    t31 = $[31];
  }
  let t32;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Route path="/seller-clients" element={<PromosAccess><SellerClientsPage /></PromosAccess>} />;
    $[32] = t32;
  } else {
    t32 = $[32];
  }
  let t33;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Route path="/promos" element={<PromosAccess><PromosPage /></PromosAccess>} />;
    $[33] = t33;
  } else {
    t33 = $[33];
  }
  let t34;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Route path="/promos/partners/:id" element={<PromosAccess><PartnerDetailPage /></PromosAccess>} />;
    $[34] = t34;
  } else {
    t34 = $[34];
  }
  let t35;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Route path="/promos/ugc/:id" element={<PromosAccess><UgcArticleDetailPage /></PromosAccess>} />;
    $[35] = t35;
  } else {
    t35 = $[35];
  }
  let t36;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Route path="/promos/:code" element={<PromosAccess><PromoDetailPage /></PromosAccess>} />;
    $[36] = t36;
  } else {
    t36 = $[36];
  }
  let t37;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Route path="/ad-campaigns" element={page("page.ads", <AdCampaignsPage />)} />;
    $[37] = t37;
  } else {
    t37 = $[37];
  }
  let t38;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <Route path="/ad-campaigns/:id" element={page("page.ads", <AdCampaignDetailPage />)} />;
    $[38] = t38;
  } else {
    t38 = $[38];
  }
  let t39;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <Route path="/shops" element={page("page.shops", <ShopsPage />)} />;
    $[39] = t39;
  } else {
    t39 = $[39];
  }
  let t40;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <Route path="/shops/seller/:userId" element={page("page.shops", <SellerDetailPage />)} />;
    $[40] = t40;
  } else {
    t40 = $[40];
  }
  let t41;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <Route path="/shops/:slug" element={page("page.shops", <ShopDetailPage />)} />;
    $[41] = t41;
  } else {
    t41 = $[41];
  }
  let t42;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <Route path="/users" element={page("page.users", <UsersPage />)} />;
    $[42] = t42;
  } else {
    t42 = $[42];
  }
  let t43;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <Route path="/top-buyers" element={page("page.users", <TopBuyersPage />)} />;
    $[43] = t43;
  } else {
    t43 = $[43];
  }
  let t44;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <Route path="/users/:id" element={page("page.users", <UserDetailPage />)} />;
    $[44] = t44;
  } else {
    t44 = $[44];
  }
  let t45;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <Route path="/delivery" element={page("page.delivery", <DeliverySettingsPage />)} />;
    $[45] = t45;
  } else {
    t45 = $[45];
  }
  let t46;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <Route path="/geo" element={page("page.geo", <GeoSettingsPage />)} />;
    $[46] = t46;
  } else {
    t46 = $[46];
  }
  let t47;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <Route path="/integrations" element={page("page.integrations", <IntegrationsPage />)} />;
    $[47] = t47;
  } else {
    t47 = $[47];
  }
  let t48;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <Route path="/support" element={page("page.support", <SupportPage />)} />;
    $[48] = t48;
  } else {
    t48 = $[48];
  }
  let t49;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <Route path="/platform-roles" element={page("page.platform_roles", <PlatformRolesPage />)} />;
    $[49] = t49;
  } else {
    t49 = $[49];
  }
  let t50;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <Route path="/audit" element={page("action.audit.view", <AuditPage />)} />;
    $[50] = t50;
  } else {
    t50 = $[50];
  }
  let t51;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <Routes>{t0}<Route element={t1}>{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t24}{t25}{t26}{t27}{t28}{t29}{t30}{t31}{t32}{t33}{t34}{t35}{t36}{t37}{t38}{t39}{t40}{t41}{t42}{t43}{t44}{t45}{t46}{t47}{t48}{t49}{t50}<Route path="*" element={<NotFoundPage />} /></Route></Routes>;
    $[51] = t51;
  } else {
    t51 = $[51];
  }
  return t51;
}